/*
 * Become a partner (/apply).
 *
 * Public. Sends POST /partner/apply; the Wealthoak team reviews it in the
 * admin panel and, on approval, emails the sign-in invite. Until then the
 * applicant cannot sign in.
 *
 * The server answers the same for a new email and one that is already a
 * partner, so the confirmation says "received", never "account created".
 *
 * PAN and GSTIN are required: Wealthoak needs them for the partner's
 * commission invoices and TDS. Both are upper-cased and stripped of spaces as
 * they are typed; the server checks the same formats.
 */

import { useEffect, useState } from "react";
import { applyToPartner, errorMessage } from "../api/client";
import BrandMark from "../ui/BrandMark";
import { go } from "./route";
import "./Login.css";

const MESSAGE_MAX = 1000;
const PHONE = /^\+?[0-9][0-9 ()-]{6,18}[0-9]$/;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PAN = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
const GSTIN = /^[0-9]{2}[A-Z0-9]{10}[0-9A-Z]Z[0-9A-Z]$/;

/**
 * PAN and GSTIN lengths. Cut after spaces are removed, not with the input's
 * maxLength, so pasting "abcde 1234f" keeps its last letter.
 */
const TAX_ID_LENGTH = { pan: 10, gstin: 15 } as const;

/** "abcde 1234f" -> "ABCDE1234F". */
const taxId = (v: string, length: number) => v.toUpperCase().replace(/\s+/g, "").slice(0, length);

type Form = {
  full_name: string;
  email: string;
  phone: string;
  pan: string;
  gstin: string;
  city: string;
  company_name: string;
  message: string;
  consent: boolean;
  website: string;
};

const EMPTY: Form = {
  full_name: "", email: "", phone: "", pan: "", gstin: "", city: "", company_name: "", message: "",
  consent: false, website: "",
};

type Errors = Partial<Record<keyof Form, string>>;

function validate(f: Form): Errors {
  const e: Errors = {};
  if (f.full_name.trim().length < 2) e.full_name = "Enter your full name.";
  if (!EMAIL.test(f.email.trim())) e.email = "Enter a valid email address.";
  if (!PHONE.test(f.phone.trim())) e.phone = "Enter a valid phone number, e.g. +91 98200 12345.";
  if (!PAN.test(f.pan)) e.pan = "Enter your PAN, e.g. ABCDE1234F.";
  if (!GSTIN.test(f.gstin)) e.gstin = "Enter your 15-character GSTIN, e.g. 27ABCDE1234F1Z5.";
  if (!f.consent) e.consent = "Please agree to be contacted about your application.";
  return e;
}

/** FastAPI validation messages arrive as "Value error, …". */
const clean = (msg: string) => msg.replace(/^Value error,\s*/i, "");

export default function Apply() {
  const [form, setForm] = useState<Form>(EMPTY);
  const [errors, setErrors] = useState<Errors>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  useEffect(() => {
    document.title = "Become a partner · Wealthoak Partner";
  }, []);

  function set<K extends keyof Form>(key: K, value: Form[K]) {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const found = validate(form);
    setErrors(found);
    if (Object.keys(found).length) return;
    setBusy(true);
    setError(null);
    try {
      const res = await applyToPartner({
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        pan: form.pan,
        gstin: form.gstin,
        city: form.city.trim() || undefined,
        company_name: form.company_name.trim() || undefined,
        message: form.message.trim() || undefined,
        consent: form.consent,
        website: form.website || undefined,
      });
      setDone(res.message);
    } catch (err) {
      setError(clean(errorMessage(err)));
    } finally {
      setBusy(false);
    }
  }

  const field = (key: "full_name" | "email" | "phone" | "pan" | "gstin" | "city" | "company_name", label: string,
                 props: React.InputHTMLAttributes<HTMLInputElement>, optional = false) => (
    <div className="auth__field">
      <label className="auth__label" htmlFor={`apply-${key}`}>
        {label}{optional && <span className="auth__optional"> (optional)</span>}
      </label>
      <input
        id={`apply-${key}`}
        className="auth__input"
        value={form[key]}
        aria-invalid={Boolean(errors[key])}
        aria-describedby={errors[key] ? `apply-${key}-err` : undefined}
        onChange={(e) => set(key, key === "pan" || key === "gstin"
          ? taxId(e.target.value, TAX_ID_LENGTH[key]) : e.target.value)}
        {...props}
      />
      {errors[key] && <p id={`apply-${key}-err`} className="auth__field-error">{errors[key]}</p>}
    </div>
  );

  const brand = (mobile: boolean) => (
    <div className={`auth__brand${mobile ? " auth__brand--mobile" : ""}`}>
      <BrandMark size={34} />
      <span className="auth__wordmark">Wealthoak</span>
      <span className="auth__tag">Partner</span>
    </div>
  );

  return (
    <div className="auth">
      <aside className="auth__aside">
        {brand(false)}
        <div>
          <h1 className="auth__headline">Grow with Wealthoak.</h1>
          <p className="auth__sub">
            Introduce clients to Wealthoak and earn a share of their
            subscription fees. Follow every referral and withdraw your
            commission to your bank from the partner panel.
          </p>
          <ol className="auth__steps">
            <li><strong>Apply</strong> with a few details about you.</li>
            <li><strong>We review</strong> your application and agree your commission.</li>
            <li><strong>Get your invite</strong> by email and start referring.</li>
          </ol>
        </div>
        <p className="auth__footnote">Wealthoak Advisory LLP · Pune, Maharashtra</p>
      </aside>

      <main className="auth__panel">
        <div className="auth__card auth__card--wide">
          {brand(true)}

          {done ? (
            <div role="status">
              <h2 className="auth__title">Application received</h2>
              <p className="auth__hint">{done}</p>
              <p className="auth__hint" style={{ marginTop: 10 }}>
                You can sign in once your application is approved; the invite
                comes to the email address you gave.
              </p>
              <button type="button" className="auth__submit" style={{ marginTop: 22 }} onClick={() => go("/")}>
                Back to sign in
              </button>
            </div>
          ) : (
            <form onSubmit={submit} noValidate>
              <h2 className="auth__title">Become a partner</h2>
              <p className="auth__hint">
                Tell us about yourself. The Wealthoak team reviews every
                application and gets back to you by email.
              </p>

              {field("full_name", "Full name", { autoComplete: "name", required: true, maxLength: 120 })}
              {field("email", "Email", { type: "email", autoComplete: "email", required: true, placeholder: "you@example.com" })}
              {field("phone", "Phone", { type: "tel", autoComplete: "tel", required: true, maxLength: 30, placeholder: "+91 98200 12345" })}
              <div className="auth__pair">
                {field("city", "City", { autoComplete: "address-level2", maxLength: 80 }, true)}
                {field("company_name", "Firm or company", { autoComplete: "organization", maxLength: 160 }, true)}
              </div>
              <div className="auth__pair">
                {field("pan", "PAN", {
                  required: true, placeholder: "ABCDE1234F",
                  autoComplete: "off", autoCapitalize: "characters", spellCheck: false,
                })}
                {field("gstin", "GSTIN", {
                  required: true, placeholder: "27ABCDE1234F1Z5",
                  autoComplete: "off", autoCapitalize: "characters", spellCheck: false,
                })}
              </div>

              <div className="auth__field">
                <label className="auth__label" htmlFor="apply-message">
                  How would you refer clients?<span className="auth__optional"> (optional)</span>
                </label>
                <textarea
                  id="apply-message"
                  className="auth__input auth__textarea"
                  rows={4}
                  maxLength={MESSAGE_MAX}
                  value={form.message}
                  onChange={(e) => set("message", e.target.value)}
                  placeholder="e.g. I'm a financial advisor with 40 client families."
                />
                <p className="auth__field-hint">{form.message.length}/{MESSAGE_MAX}</p>
              </div>

              {/* Honeypot: off-screen and skipped by keyboard and screen readers. */}
              <div className="auth__hp" aria-hidden="true">
                <label htmlFor="apply-website">Website</label>
                <input id="apply-website" tabIndex={-1} autoComplete="off" value={form.website}
                  onChange={(e) => set("website", e.target.value)} />
              </div>

              <label className="auth__check">
                <input
                  type="checkbox"
                  checked={form.consent}
                  aria-invalid={Boolean(errors.consent)}
                  onChange={(e) => set("consent", e.target.checked)}
                />
                <span>Wealthoak may contact me by email or phone about this application.</span>
              </label>
              {errors.consent && <p className="auth__field-error">{errors.consent}</p>}

              <button className="auth__submit" disabled={busy}>
                {busy ? "Sending…" : "Submit application"}
              </button>
              <div className="auth__row">
                <button type="button" className="auth__link" onClick={() => go("/")}>
                  Already a partner? Sign in
                </button>
              </div>
            </form>
          )}

          {error && <div className="auth__error" role="alert">{error}</div>}
        </div>
      </main>
    </div>
  );
}
